import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  LogOut,
  UserCircle,
  Building2,
  Settings,
  Users,
  Scale,
  TrendingUp,
  Phone,
  DollarSign,
  Moon,
  Sun,
  Menu,
  Search
} from "lucide-react";
import { useSidebar } from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { SearchBar } from "@/components/common/SearchBar";
import { useGlobalSearch } from "@/hooks/useGlobalSearch";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { MobileDialer } from "@/components/calls/MobileDialer";
import { DashboardAccessCodesPanel } from "@/components/dashboard/DashboardAccessCodesPanel";
import { NotificationsPanel } from "@/components/notifications/NotificationsPanel";
import { DualClock } from "@/components/common/DualClock";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { useIsMobile } from "@/hooks/use-mobile";

export default function TopBar({ currentPageName }) {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { user: authUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchInputRef = useRef(null);
  const [isDialerOpen, setIsDialerOpen] = useState(false);
  const { results, loading } = useGlobalSearch(searchQuery);
  const { toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (showSearch) {
      setTimeout(() => {
        const input = searchInputRef.current?.querySelector?.('input') || searchInputRef.current;
        input?.focus();
      }, 50);
    }
  }, [showSearch]);

    const clearInteractionLocks = () => {
    document.documentElement.style.pointerEvents = '';
    document.body.style.pointerEvents = '';
    if (root) root.style.pointerEvents = '';
    document.documentElement.removeAttribute('data-scroll-locked');
    document.body.removeAttribute('data-scroll-locked');
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.paddingRight = '';
  };

  const navigateFromUserMenu = (targetUrl) => {
    // Prevent stale Radix dropdown locks when navigating from avatar menu on mobile PWA.
    // Only run cleanup on mobile — desktop Radix handles its own locks correctly.
    if (isMobile) {
      clearInteractionLocks();
    }
    window.requestAnimationFrame(() => {
      navigate(targetUrl);
      if (isMobile) {
        window.setTimeout(clearInteractionLocks, 80);
        window.setTimeout(clearInteractionLocks, 260);
      }
    });
  };

  const user = authUser ? {
    full_name: authUser.user_metadata?.display_name || authUser.email,
    email: authUser.email,
  } : null;

  const handleLogout = async () => {
    await signOut();
  };

  const handleSelectResult = (result) => {
    setSearchQuery("");
    setIsSearchOpen(false);
    setShowSearch(false);
    
    switch (result.type) {
      case 'lead':
        navigate(createPageUrl("LeadDetail") + "?id=" + result.id);
        break;
      case 'contact':
        navigate(createPageUrl("ContactDetail") + "?id=" + result.id);
        break;
      case 'deal':
        navigate(createPageUrl("DealDetail") + "?id=" + result.id);
        break;
      case 'case':
        navigate(createPageUrl("CaseDetail") + "?id=" + result.id);
        break;
    }
  };

  const getResultIcon = (type) => {
    switch (type) {
      case 'lead':
        return <TrendingUp className="h-4 w-4" />;
      case 'contact':
        return <Users className="h-4 w-4" />;
      case 'deal':
        return <DollarSign className="h-4 w-4" />;
      case 'case':
        return <Scale className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getResultTypeLabel = (type) => {
    switch (type) {
      case 'lead':
        return t('navigation.leads');
      case 'contact':
        return t('navigation.contacts');
      case 'deal':
        return t('navigation.deals');
      case 'case':
        return t('navigation.cases');
      default:
        return '';
    }
  };

  useEffect(() => {
    setIsSearchOpen(searchQuery.length >= 2);
  }, [searchQuery]);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pt-safe">
      <div className="flex min-h-16 items-center px-4 gap-2 md:gap-4 py-2">
        {/* Mobile Menu Button */}
        <Button 
          variant="ghost" 
          size="icon"
          className="md:hidden flex-shrink-0"
          onClick={toggleSidebar}
        >
          <Menu className="h-5 w-5" />
        </Button>
        {/* Collapsible Search */}
        {!showSearch ? (
          <Button
            variant="ghost"
            size="icon"
            className="flex-shrink-0 rounded-full"
            onClick={() => setShowSearch(true)}
          >
            <Search className="h-5 w-5" />
          </Button>
        ) : (
          <div ref={searchInputRef} className="w-full max-w-md animate-in fade-in slide-in-from-left-2 duration-300">
          <Popover open={isSearchOpen} onOpenChange={setIsSearchOpen}>
            <PopoverTrigger asChild>
              <div>
                <SearchBar
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onBlur={() => {
                    if (!searchQuery) {
                      setTimeout(() => setShowSearch(false), 150);
                    }
                  }}
                  placeholder={t('topbar.search')}
                />
              </div>
            </PopoverTrigger>
            <PopoverContent 
              className="w-[400px] p-0" 
              align="start"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <Command>
                <CommandList
                  onWheel={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                >
                  {loading ? (
                    <div className="p-4 text-sm text-muted-foreground text-center">
                      {t('common.loading')}
                    </div>
                  ) : results.length === 0 ? (
                    <CommandEmpty>{t('common.noResults')}</CommandEmpty>
                  ) : (
                    <>
                      <CommandGroup heading={t('topbar.results')}>
                        {results.map((result) => (
                          <CommandItem
                            key={`${result.type}-${result.id}`}
                            onSelect={() => handleSelectResult(result)}
                            className="cursor-pointer"
                          >
                            <div className="flex items-center gap-3 w-full">
                              {getResultIcon(result.type)}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium truncate">
                                    {result.name}
                                  </p>
                                  <Badge variant="secondary" className="text-xs">
                                    {getResultTypeLabel(result.type)}
                                  </Badge>
                                </div>
                                {result.subtitle && (
                                  <p className="text-xs text-muted-foreground truncate">
                                    {result.subtitle}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
         </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right Actions */}
        <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
          {/* Dual Clock - hidden on mobile */}
          <div className="hidden md:block">
            <DualClock />
          </div>

          {/* Theme Toggle - hidden on mobile */}
          <Button 
            variant="ghost" 
            size="icon"
            className="hidden md:flex"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          >
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          {/* Dialer - hidden on mobile */}
          <Button 
            variant="ghost" 
            size="icon"
            className="hidden md:flex"
            onClick={() => setIsDialerOpen(true)}
          >
            <Phone className="h-5 w-5" />
          </Button>

          {/* Dashboard Access Codes - hidden on mobile */}
          <div className="hidden md:block">
            <DashboardAccessCodesPanel />
          </div>

          {/* Notifications - Unified Panel */}
          <ErrorBoundary>
            <NotificationsPanel />
          </ErrorBoundary>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 px-2">
                <Avatar className="h-8 w-8 flex-shrink-0">
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {user?.full_name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden lg:block text-sm font-medium truncate">
                  {user?.full_name?.split(' ')[0] || "User"}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user?.full_name || "User"}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigateFromUserMenu(createPageUrl("Profile"))} className="cursor-pointer">
                <UserCircle className="w-4 h-4 mr-2" />
                {t('navigation.profile')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigateFromUserMenu(createPageUrl("Settings"))} className="cursor-pointer">
                <Settings className="w-4 h-4 mr-2" />
                {t('navigation.settings')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigateFromUserMenu(createPageUrl("CompanySettings"))} className="cursor-pointer">
                <Building2 className="w-4 h-4 mr-2" />
                {t('navigation.companySettings')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={handleLogout} className="text-destructive cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" />
                {t('common.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile Dialer */}
      <MobileDialer isOpen={isDialerOpen} onClose={() => setIsDialerOpen(false)} />
    </header>
  );
}
